import {
  forwardRef,
  ForwardedRef,
  useRef,
  useState,
  useMemo,
  useImperativeHandle,
  CSSProperties,
  useEffect,
} from 'react';
import { Upload, UploadProps, Input } from 'antd';

import cls from 'classnames';

import { message } from '../../message';
import { uploadImageFile } from '../../../utils/upload';
import { attachmentCheck } from '../../../utils/attachment';
import resetIcon from '../../../assets/Chat/reset.svg';
import uploadIcon from '../../../assets/Chat/upload.svg';
import loadingIcon from '../../../assets/Chat/loading.svg';
import sendIcon from '../../../assets/Chat/send.svg';
import deleteIcon from '../../../assets/Chat/delete.svg';
import {
  AttachmentImage,
  ConversationAttachmentType,
  EPrivacySetting,
  IMessage,
  MessageType,
  NpcFieldMap,
  NpcFieldType,
  RoleType,
} from '../../../types';

import _styles from './index.module.scss';
import { audioLoadingIcon } from '../../../assets';
import { useTranslation } from 'next-i18next';
import { Global_Image_Memory } from '../../../state/image';
import { LocalImage } from '../../Image';
import { track } from '../../../track';
import { useAppSelector } from '../../../state';
import { getNpcStatus } from '../../Home/NpcList';
import { getDialogModelChoice } from '../../EditHeader';

const PAGE = 'create_npc_advanced_page';

const formatAttachmentRData = (data: InterAttachment | null | undefined) => {
  if (data?.desc && data?.url) {
    return {
      desc: data.desc,
      url: data.url,
    };
  }
  return null;
};

interface InterAttachment {
  aborted?: boolean;
  desc?: string;
  url?: string;
  width?: number;
  height?: number;
}
interface ISendboxProps {
  styles: CSSProperties;
  inputPlaceholder?: string;
  loading?: boolean;
  fromWorkspace?: boolean;
  uploadParams?: {
    cdnPrefix?: string;
    userId: number;
    npcId: number;
  };
  onSendMessage: (val: string, attachment?: AttachmentImage | null) => void;
  onResetChat: () => void;
  onUploadImage?: () => void;
  appendCustomAsideMessage?: (asideMsg: IMessage) => void;
  safeCheck: () => Promise<NpcFieldType[]>;
  checking: boolean;
  disabled?: boolean;
  setIsSendingImage: (val: boolean) => void;
}

export const Sendbox = forwardRef(
  (
    {
      inputPlaceholder,
      uploadParams,
      fromWorkspace,
      onSendMessage,
      onResetChat,
      appendCustomAsideMessage,
      checking,
      safeCheck,
      disabled,
      setIsSendingImage,
    }: ISendboxProps,
    ref: ForwardedRef<any>
  ) => {
    // const { t } = useTranslation();
    const [inputValue, setInputValue] = useState<string>('');
    const [previewAttachmentImage, setPreviewAttachmentImage] = useState<string>('');
    const [uploadLoading, setUploadLoading] = useState<boolean>(false);

    const isComposition = useRef<boolean>(false); // input输入法判断 会引起safari无法发送，先注释
    const attachmentImageRef = useRef<InterAttachment | null>(null);

    const {
      npcId,
      statusInfo,
      settingInfo: { chat_model_type },
    } = useAppSelector((state) => state.edit);

    const uploadProps: UploadProps = useMemo(
      () => ({
        name: 'file',
        accept: 'image/png,image/jpg,image/jpeg',
        customRequest: async (options) => {
          setUploadLoading(true);
          const { file } = options;

          const reader = new FileReader();
          reader.readAsDataURL(file as Blob);
          reader.onload = () => {
            setPreviewAttachmentImage(reader.result as string);
          };

          attachmentImageRef.current = {
            aborted: false,
            desc: '',
            url: '',
          };

          try {
            const { url } = await uploadImageFile({
              userId: uploadParams?.userId as number,
              file: file as File,
              privacy_setting: EPrivacySetting.Private,
            });

            const { image_desc } = await attachmentCheck({
              npcId: uploadParams?.npcId as number,
              attachmentType: ConversationAttachmentType.Image,
              fromWorkspace,
              image: {
                url,
              },
            });

            if (attachmentImageRef.current.aborted) {
              attachmentImageRef.current = null;
              setPreviewAttachmentImage('');

              return;
            }

            attachmentImageRef.current = {
              desc: image_desc,
              url,
            };
            console.log('setUrl', url);
            Global_Image_Memory[url] = file as File;
          } catch (err: any) {
            setPreviewAttachmentImage('');
            attachmentImageRef.current = null;
            message.error(err?.status_msg || err?.message);
          } finally {
            setUploadLoading(false);
          }
        },
        showUploadList: false,
      }),
      [fromWorkspace, uploadParams?.npcId, uploadParams?.userId]
    );

    const clearAttachment = () => {
      attachmentImageRef.current = null;
      setPreviewAttachmentImage('');
    };

    useImperativeHandle(ref, () => ({
      setInputValue,
      clearAttachment,
    }));

    const onInput = (e: any) => {
      const value = e.target.value;
      setInputValue(value);
    };

    const onFocusInput = () => {};

    const { t } = useTranslation();
    const sendMessageWithSafeCheck = async () => {
      if (!inputValue && !attachmentImageRef.current?.url) return;
      if (checking) return;
      const notPassFields = await safeCheck();
      if (notPassFields.length > 0) {
        const notPassText = notPassFields.map((key) => t(NpcFieldMap[key] || 'default_npc_info'));
        appendCustomAsideMessage?.({
          txt: notPassText.join(' ') + ' ' + t('not_pass_text'),
          type: MessageType.Aside,
          mid: Date.now() + '',
          role_type: RoleType.AI,
        });
      } else {
        onSendMessage(inputValue, formatAttachmentRData(attachmentImageRef.current));
      }
    };

    const onKeyup = (e: any) => {
      const code = e.keyCode;
      if (code === 13 && !isComposition.current) {
        e.preventDefault();
        sendMessageWithSafeCheck();
      }
    };

    const onkeydown = (e: any) => {
      const code = e.keyCode;
      const key = e.key;
      if (code === 13 && !isComposition.current) {
        e.preventDefault();
      }

      if (key === 'Enter' && e.shiftKey) {
        e.preventDefault();
      }
    };

    const onCompositionStart = () => {
      isComposition.current = true;
    };

    const onCompositionEnd = () => {
      isComposition.current = false;
    };

    const onClickSend = () => {
      sendMessageWithSafeCheck();
    };

    const onClickResetChat = () => {
      onResetChat();
    };

    const onClickUploadImage = () => {
      if (previewAttachmentImage) return;
      track.send('send_image_btn_click', PAGE, {
        npc_id: npcId,
        dialog_model_choice: chat_model_type,
        ugc_npc_approving_status: getNpcStatus(statusInfo),
      });
    };

    const onClickRemoveUploadImage = () => {
      setPreviewAttachmentImage('');
      attachmentImageRef.current = {
        aborted: true,
        desc: '',
      };
    };

    useEffect(() => {
      setIsSendingImage(!!previewAttachmentImage);
    }, [previewAttachmentImage]);
    return (
      <div
        id="input-container"
        className={cls([_styles.inputContainer], { [_styles.disabled]: disabled })}
      >
        <div className={cls([_styles.action])} onClick={onClickResetChat}>
          <LocalImage src={resetIcon} />
          <p>{t('edit_chat_preview_reset_btn_txt')}</p>
        </div>
        <div className={cls([_styles.inputControl, !inputValue ? _styles.inputControlEmpty : ''])}>
          <div className={cls([_styles.inputWrapper, !inputValue ? _styles.inputEmpty : ''])}>
            <Input.TextArea
              className={_styles.input}
              value={inputValue}
              placeholder={inputPlaceholder}
              autoSize={{
                minRows: 1,
                maxRows: 3,
              }}
              autoComplete={'off'}
              onInput={onInput}
              onKeyUp={onKeyup}
              onKeyDown={onkeydown}
              onCompositionStart={onCompositionStart}
              onCompositionEnd={onCompositionEnd}
              onFocus={onFocusInput}
              disabled={uploadLoading}
            />
          </div>
          <Upload
            {...uploadProps}
            className={cls([
              _styles.sendBtn,
              (uploadLoading || previewAttachmentImage) && _styles.disableBtn,
            ])}
            disabled={!!previewAttachmentImage}
          >
            <div onClick={onClickUploadImage}>
              <LocalImage src={uploadIcon} />
            </div>
          </Upload>
          <div className={_styles.btnSplit}></div>
          <div
            className={cls([_styles.sendBtn], {
              [_styles.loading]: checking,
              [_styles.disableBtn]: (!inputValue && !previewAttachmentImage) || uploadLoading,
            })}
            onClick={onClickSend}
          >
            <LocalImage key={checking + ''} src={checking ? audioLoadingIcon : sendIcon} />
          </div>
          {previewAttachmentImage ? (
            <div className={_styles.uploadControl}>
              <div className={_styles.imageItem}>
                <img className={_styles.previewImage} src={previewAttachmentImage} />
                <LocalImage
                  className={_styles.deleteIcon}
                  src={deleteIcon}
                  onClick={onClickRemoveUploadImage}
                />
                {uploadLoading ? (
                  <LocalImage className={_styles.loadingIcon} src={loadingIcon} />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
);
